import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Divider,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogContent,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Person,
  Email,
  Phone,
  LocationOn,
  CalendarToday,
  Badge as BadgeIcon,
  CreditCard,
  CheckCircle,
  Download,
  EmojiEvents,
  Verified,
  Pending,
  Cancel,
  MoreVert,
  Logout,
  Receipt,
  Home,
  Close as CloseIcon,
  FamilyRestroom,
} from '@mui/icons-material';
import { athleteAPI } from '../api/athlete.api';
import { paymentAPI } from '../api/payment.api';
import { useAuth } from '../context/AuthContext';

const PageWrapper = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#f8f9fa',
  padding: '2rem 0',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(41, 30, 108, 0.1)',
  marginBottom: theme.spacing(3),
  border: '1px solid #e0e0e0',
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: 'rgba(41, 30, 108, 0.05)',
  borderLeft: `4px solid #291e6c`,
}));

const StatusCard = styled(Card)(({ theme }) => ({
  height: '100%',
  border: '1px solid #e0e0e0',
  borderRadius: theme.spacing(1.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(41, 30, 108, 0.1)',
    transform: 'translateY(-2px)',
  },
}));

const StyledButton = styled(Button)({
  padding: '10px 24px',
  borderRadius: '8px',
  fontSize: '0.95rem',
  fontWeight: 600,
  textTransform: 'none',
  fontFamily: "'Oswald', sans-serif",
});

const ImagePreview = styled('img')({
  width: '100%',
  maxWidth: '200px',
  height: 'auto',
  borderRadius: '8px',
  border: '2px solid #e0e0e0',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
  },
});

const InfoRow = ({ icon, label, value }) => (
  <TableRow>
    <TableCell sx={{ borderBottom: '1px solid #f0f0f0', py: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#555' }}>
          {label}
        </Typography>
      </Box>
    </TableCell>
    <TableCell sx={{ borderBottom: '1px solid #f0f0f0', py: 1.5 }}>
      <Typography variant="body2" sx={{ color: '#333' }}>
        {value}
      </Typography>
    </TableCell>
  </TableRow>
);

const Dashboard = () => {
  const { isAuthenticated, user, logout, loading: isLoading } = useAuth();
  const navigate = useNavigate();
  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [admitCardStatus, setAdmitCardStatus] = useState(null);
  const [downloadingCard, setDownloadingCard] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchAthleteProfile();
    fetchAdmitCardStatus();
  }, []);

  if (isLoading) {
      return <div>Loading...</div>; // or a spinner component
    }

  const fetchAthleteProfile = async () => {
    try {
      const response = await athleteAPI.getProfile();
      if (response.success) {
        setAthlete(response.data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile data');
      if (error.response?.status === 401) {
        localStorage.clear();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmitCardStatus = async () => {
    try {
      const response = await athleteAPI.getAdmitCardStatus();
      if (response.success) {
        setAdmitCardStatus(response.data);
      }
    } catch (error) {
      console.error('Error fetching admit card status:', error);
    }
  };
const handleDownloadAdmitCard = async () => {
  setDownloadingCard(true);
  try {
    // API now returns JSON with URL
    const response = await athleteAPI.downloadAdmitCard();
    
    if (response.data.url) {
      const { url, filename } = response.data;
      
      // Create invisible link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      link.setAttribute('target', '_blank');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Admit card downloaded successfully!');
    }
  } catch (error) {
    console.error('Error downloading admit card:', error);
    toast.error(error.response?.data?.message || 'Failed to download admit card');
  } finally {
    setDownloadingCard(false);
  }
};

const handleDownloadInvoice = async () => {
  setDownloadingInvoice(true);
  try {
    // API now returns JSON with URL
    const response = await paymentAPI.downloadInvoice(athlete._id);
    
    if (response.data.url) {
      const { url, filename } = response.data;
      
      // Create invisible link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      link.setAttribute('target', '_blank');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Invoice downloaded successfully');
    }
  } catch (error) {
    console.error('Error downloading invoice:', error);
    toast.error(error.response?.data?.message || 'Failed to download invoice');
  } finally {
    setDownloadingInvoice(false);
  }
};


  const handleLogout = () => {
    localStorage.clear();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('https://') || path.startsWith('http://')) return path;
    const baseUrl = 'https://register.iwlf.in/api';
    return baseUrl.replace('/api', path);
  };

  const getVerificationStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <Verified sx={{ color: '#4caf50', fontSize: 28 }} />;
      case 'rejected':
        return <Cancel sx={{ color: '#f44336', fontSize: 28 }} />;
      default:
        return <Pending sx={{ color: '#ff9800', fontSize: 28 }} />;
    }
  };

  const getVerificationStatusChip = (status) => {
    switch (status) {
      case 'approved':
        return <Chip icon={<CheckCircle />} label="Approved" color="success" sx={{ fontWeight: 600 }} />;
      case 'rejected':
        return <Chip icon={<Cancel />} label="Rejected" color="error" sx={{ fontWeight: 600 }} />;
      default:
        return <Chip icon={<Pending />} label="Pending" color="warning" sx={{ fontWeight: 600 }} />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return (amount / 100).toFixed(2);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} sx={{ color: '#291e6c' }} />
      </Box>
    );
  }

  if (!athlete) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error">
          Failed to load athlete data. Please try logging in again.
        </Alert>
        <Button
          variant="contained"
          fullWidth
          onClick={() => navigate('/login')}
          sx={{ mt: 2, backgroundColor: '#291e6c' }}
        >
          Go to Login
        </Button>
      </Container>
    );
  }

  return (
    <PageWrapper>
      <Container maxWidth="xl">
        {/* Header with Menu */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 800,
                color: '#291e6c',
              }}
            >
              ATHLETE DASHBOARD
            </Typography>
            <Typography variant="body1" sx={{ color: '#666' }}>
              Welcome back, {athlete.firstName}!
            </Typography>
          </Box>
          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              backgroundColor: 'rgba(41, 30, 108, 0.1)',
              '&:hover': { backgroundColor: 'rgba(41, 30, 108, 0.2)' },
            }}
          >
            <MoreVert />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 1 }} /> Logout
            </MenuItem>
          </Menu>
        </Box>
        <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
            <StatusCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <BadgeIcon sx={{ color: 'primary', fontSize: 28 }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                      Your Athlete ID
                    </Typography>
                    <Chip
                icon={<BadgeIcon />}
                label={athlete._id}
                color="primary"
                sx={{ fontFamily: "'Oswald', sans-serif", fontWeight: 600, px: 2, py: 2.5 }}
              />
                  </Box>
                </Box>
              </CardContent>
            </StatusCard>
          </Grid>
          {/* Status Cards Row */}
          <Grid item xs={12} md={3}>
            <StatusCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  {getVerificationStatusIcon(athlete.verificationStatus)}
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                      Verification Status
                    </Typography>
                    {getVerificationStatusChip(athlete.verificationStatus)}
                  </Box>
                </Box>
              </CardContent>
            </StatusCard>
          </Grid>

          <Grid item xs={12} md={3}>
            <StatusCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <CreditCard sx={{ color: '#4caf50', fontSize: 28 }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                      Payment Status
                    </Typography>
                    <Chip
                      icon={<CheckCircle />}
                      label={athlete.paymentStatus === 'completed' ? 'Completed' : 'Pending'}
                      color={athlete.paymentStatus === 'completed' ? 'success' : 'warning'}
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </StatusCard>
          </Grid>

          <Grid item xs={12} md={3  }>
            <StatusCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <BadgeIcon sx={{ color: '#291e6c', fontSize: 28 }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                      Registration Type
                    </Typography>
                    <Chip
                      label={athlete.registrationType.charAt(0).toUpperCase() + athlete.registrationType.slice(1)}
                      color="primary"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </StatusCard>
          </Grid>

          {/* Main Content - Left Column */}
          <Grid item xs={12} md={7}>
            {/* Personal Information */}
            <StyledPaper>
              <SectionHeader>
                <Person sx={{ color: '#291e6c', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700 }}>
                  Personal Information
                </Typography>
              </SectionHeader>

              {/* Profile Photo */}
              {athlete.photo && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Profile Photo
                  </Typography>
                  <ImagePreview
                    src={getImageUrl(athlete.photo.path)}
                    alt="Profile"
                    onClick={() => setImagePreview({ url: getImageUrl(athlete.photo.path), title: 'Profile Photo' })}
                  />
                </Box>
              )}

              <TableContainer>
                <Table>
                  <TableBody>
                    <InfoRow
                      icon={<Person fontSize="small" sx={{ color: '#291e6c' }} />}
                      label="Full Name"
                      value={`${athlete.firstName} ${athlete.middleName || ''} ${athlete.surname}`.trim()}
                    />
                    <InfoRow
                      icon={<Person fontSize="small" sx={{ color: '#291e6c' }} />}
                      label="Father's Name"
                      value={`${athlete.fatherFirstName} ${athlete.fatherMiddleName || ''} ${athlete.fatherSurname}`.trim()}
                    />
                    {athlete.motherFirstName && (
                      <InfoRow
                        icon={<Person fontSize="small" sx={{ color: '#291e6c' }} />}
                        label="Mother's Name"
                        value={`${athlete.motherFirstName} ${athlete.motherMiddleName || ''} ${athlete.motherSurname || ''}`.trim()}
                      />
                    )}
                    <InfoRow
                      icon={<CalendarToday fontSize="small" sx={{ color: '#291e6c' }} />}
                      label="Date of Birth"
                      value={formatDate(athlete.birthDate)}
                    />
                    <InfoRow
                      icon={<Person fontSize="small" sx={{ color: '#291e6c' }} />}
                      label="Gender"
                      value={athlete.gender.charAt(0).toUpperCase() + athlete.gender.slice(1)}
                    />
                    <InfoRow
                      icon={<BadgeIcon fontSize="small" sx={{ color: '#291e6c' }} />}
                      label="Blood Group"
                      value={athlete.bloodGroup || 'Not specified'}
                    />
                    <InfoRow
                      icon={<BadgeIcon fontSize="small" sx={{ color: '#291e6c' }} />}
                      label="Registration Type"
                      value={athlete.registrationType.charAt(0).toUpperCase() + athlete.registrationType.slice(1)}
                    />
                  </TableBody>
                </Table>
              </TableContainer>
            </StyledPaper>

            {/* Contact Information */}
            <StyledPaper>
              <SectionHeader>
                <Phone sx={{ color: '#291e6c', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700 }}>
                  Contact Information
                </Typography>
              </SectionHeader>

              <TableContainer>
                <Table>
                  <TableBody>
                    <InfoRow
                      icon={<Email fontSize="small" sx={{ color: '#291e6c' }} />}
                      label="Email"
                      value={athlete.email}
                    />
                    <InfoRow
                      icon={<Phone fontSize="small" sx={{ color: '#291e6c' }} />}
                      label="Phone Number"
                      value={athlete.phoneNumber}
                    />
                    <InfoRow
                      icon={<BadgeIcon fontSize="small" sx={{ color: '#291e6c' }} />}
                      label="Aadhar Number"
                      value={athlete.aadharCardNumber}
                    />
                    {athlete.kheloIndiaId && (
                      <InfoRow
                        icon={<EmojiEvents fontSize="small" sx={{ color: '#291e6c' }} />}
                        label="Khelo India ID"
                        value={athlete.kheloIndiaId}
                      />
                    )}
                    {athlete.district && (
                      <InfoRow
                        icon={<LocationOn fontSize="small" sx={{ color: '#291e6c' }} />}
                        label="District"
                        value={athlete.district}
                      />
                    )}
                    {athlete.stateDepartment && (
                      <InfoRow
                        icon={<LocationOn fontSize="small" sx={{ color: '#291e6c' }} />}
                        label="State Department"
                        value={athlete.stateDepartment}
                      />
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </StyledPaper>

            {/* Address */}
            <StyledPaper>
              <SectionHeader>
                <Home sx={{ color: '#291e6c', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700 }}>
                  Address Details
                </Typography>
              </SectionHeader>

              <TableContainer>
                <Table>
                  <TableBody>
                    <InfoRow
                      icon={<LocationOn fontSize="small" sx={{ color: '#291e6c' }} />}
                      label="Address"
                      value={
                        <>
                          {athlete.addressLine1}
                          {athlete.addressLine2 && <><br />{athlete.addressLine2}</>}
                        </>
                      }
                    />
                    <InfoRow
                      icon={<LocationOn fontSize="small" sx={{ color: '#291e6c' }} />}
                      label="City"
                      value={athlete.city}
                    />
                    <InfoRow
                      icon={<LocationOn fontSize="small" sx={{ color: '#291e6c' }} />}
                      label="State"
                      value={athlete.state}
                    />
                    <InfoRow
                      icon={<LocationOn fontSize="small" sx={{ color: '#291e6c' }} />}
                      label="Pincode"
                      value={athlete.pincode}
                    />
                    <InfoRow
                      icon={<LocationOn fontSize="small" sx={{ color: '#291e6c' }} />}
                      label="Country"
                      value={athlete.country}
                    />
                  </TableBody>
                </Table>
              </TableContainer>
            </StyledPaper>

            {/* Documents */}
            <StyledPaper>
              <SectionHeader>
                <BadgeIcon sx={{ color: '#291e6c', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700 }}>
                  Uploaded Documents
                </Typography>
              </SectionHeader>

              {/* Identity Proof */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Identity Proof: {athlete.identityProof?.type}
                </Typography>
                <Grid container spacing={2}>
                  {athlete.identityProof?.front && (
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                        Front Side
                      </Typography>
                      <ImagePreview
                        src={getImageUrl(athlete.identityProof.front.path)}
                        alt="Identity Proof Front"
                        onClick={() => setImagePreview({
                          url: getImageUrl(athlete.identityProof.front.path),
                          title: 'Identity Proof - Front'
                        })}
                      />
                    </Grid>
                  )}
                  {athlete.identityProof?.back && (
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                        Back Side
                      </Typography>
                      <ImagePreview
                        src={getImageUrl(athlete.identityProof.back.path)}
                        alt="Identity Proof Back"
                        onClick={() => setImagePreview({
                          url: getImageUrl(athlete.identityProof.back.path),
                          title: 'Identity Proof - Back'
                        })}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Proof of Age */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Proof of Age: {athlete.proofOfAge?.type}
                </Typography>
                <Grid container spacing={2}>
                  {athlete.proofOfAge?.front && (
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                        Front Side
                      </Typography>
                      <ImagePreview
                        src={getImageUrl(athlete.proofOfAge.front.path)}
                        alt="Proof of Age Front"
                        onClick={() => setImagePreview({
                          url: getImageUrl(athlete.proofOfAge.front.path),
                          title: 'Proof of Age - Front'
                        })}
                      />
                    </Grid>
                  )}
                  {athlete.proofOfAge?.back && (
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                        Back Side
                      </Typography>
                      <ImagePreview
                        src={getImageUrl(athlete.proofOfAge.back.path)}
                        alt="Proof of Age Back"
                        onClick={() => setImagePreview({
                          url: getImageUrl(athlete.proofOfAge.back.path),
                          title: 'Proof of Age - Back'
                        })}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Address Proof */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Address Proof: {athlete.addressProof?.type}
                </Typography>
                <Grid container spacing={2}>
                  {athlete.addressProof?.front && (
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                        Front Side
                      </Typography>
                      <ImagePreview
                        src={getImageUrl(athlete.addressProof.front.path)}
                        alt="Address Proof Front"
                        onClick={() => setImagePreview({
                          url: getImageUrl(athlete.addressProof.front.path),
                          title: 'Address Proof - Front'
                        })}
                      />
                    </Grid>
                  )}
                  {athlete.addressProof?.back && (
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                        Back Side
                      </Typography>
                      <ImagePreview
                        src={getImageUrl(athlete.addressProof.back.path)}
                        alt="Address Proof Back"
                        onClick={() => setImagePreview({
                          url: getImageUrl(athlete.addressProof.back.path),
                          title: 'Address Proof - Back'
                        })}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Medical Certificate */}
              {athlete.medicalCertificate && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Medical Certificate
                  </Typography>
                  <Chip
                    label={athlete.medicalCertificate.filename}
                    icon={<CheckCircle />}
                    color="success"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              )}
            </StyledPaper>

            {/* Event Details */}
            {athlete.selectedEvent && (
              <StyledPaper>
                <SectionHeader>
                  <EmojiEvents sx={{ color: '#291e6c', fontSize: 28 }} />
                  <Typography variant="h6" sx={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700 }}>
                    Event Details
                  </Typography>
                </SectionHeader>

                <Box sx={{ backgroundColor: 'rgba(41, 30, 108, 0.03)', p: 2, borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#291e6c', mb: 2 }}>
                    {athlete.selectedEvent.eventName}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                        Event Amount
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#4caf50' }}>
                        ₹{athlete.selectedEvent.eventAmount}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                                          <Typography variant="caption" sx={{ color: '#666' }}>
                                            Duration
                                          </Typography>
                                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            Lifetime
                                          </Typography>
                                        </Grid>
                  </Grid>
                </Box>
              </StyledPaper>
            )}
          </Grid>

          {/* Right Column - Actions & Status */}
          <Grid item xs={12} md={5}>
            {/* Admit Card Section */}
            {admitCardStatus && (
              <StyledPaper>
                <Typography variant="h6" sx={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, mb: 2 }}>
                  Admit Card
                </Typography>

                {admitCardStatus.canDownload ? (
                  <>
                    <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
                      Your admit card is ready to download
                    </Alert>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                        Admit Card Number
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#291e6c' }}>
                        {admitCardStatus.admitCardNumber}
                      </Typography>
                    </Box>
                    <StyledButton
                      variant="contained"
                      fullWidth
                      startIcon={downloadingCard ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Download />}
                      onClick={handleDownloadAdmitCard}
                      disabled={downloadingCard}
                      sx={{ backgroundColor: '#291e6c', '&:hover': { backgroundColor: '#1f1750' } }}
                    >
                      {downloadingCard ? 'Downloading...' : 'Download Admit Card'}
                    </StyledButton>
                  </>
                ) : (
                  <Alert severity="info">
                    {athlete.verificationStatus === 'approved'
                      ? 'Your admit card is being generated. Please check back soon.'
                      : 'Admit card will be available after verification approval.'}
                  </Alert>
                )}
              </StyledPaper>
            )}

            {/* Payment Information */}
            {athlete.paymentStatus === 'completed' && (
              <StyledPaper>
                <Typography variant="h6" sx={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, mb: 2 }}>
                  Payment Information
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                    Invoice Number
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {athlete.invoiceNumber}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                    Payment ID
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#555' }}>
                    {athlete.paymentId}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                    Amount Paid
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#4caf50' }}>
                    ₹{formatAmount(athlete.amount)}
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                    Payment Date
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDate(athlete.paymentDate)}
                  </Typography>
                </Box>

                <StyledButton
                  variant="outlined"
                  fullWidth
                  startIcon={downloadingInvoice ? <CircularProgress size={20} /> : <Receipt />}
                  onClick={handleDownloadInvoice}
                  disabled={downloadingInvoice}
                  sx={{
                    borderColor: '#291e6c',
                    color: '#291e6c',
                    '&:hover': {
                      borderColor: '#1f1750',
                      backgroundColor: 'rgba(41, 30, 108, 0.05)',
                    },
                  }}
                >
                  {downloadingInvoice ? 'Downloading...' : 'Download Invoice'}
                </StyledButton>
              </StyledPaper>
            )}

            {/* Verification Info */}
            {athlete.verificationStatus === 'pending' && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Verification Pending
                </Typography>
                <Typography variant="caption">
                  Your documents are being reviewed by the admin. You will be notified once approved.
                </Typography>
              </Alert>
            )}

            {athlete.verificationStatus === 'rejected' && athlete.verificationNotes && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Verification Rejected
                </Typography>
                <Typography variant="caption">
                  Reason: {athlete.verificationNotes}
                </Typography>
              </Alert>
            )}
          </Grid>
        </Grid>

        {/* Image Preview Dialog */}
        <Dialog
          open={!!imagePreview}
          onClose={() => setImagePreview(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogContent sx={{ position: 'relative', p: 0 }}>
            <IconButton
              onClick={() => setImagePreview(null)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
              }}
            >
              <CloseIcon />
            </IconButton>
            {imagePreview && (
              <>
                <Typography variant="h6" sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                  {imagePreview.title}
                </Typography>
                <img
                  src={imagePreview.url}
                  alt={imagePreview.title}
                  style={{ width: '100%', height: 'auto' }}
                />
              </>
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </PageWrapper>
  );
};

export default Dashboard;
